import React from 'react';
import feedbackRadioOn from '../../assets/images/feedback-radio-on.svg';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import cashAppLogo from '../../assets/icons/cash-app-pay.svg';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import type { TippingPaymentOption } from '../components/tipping/types';

const paymentOptions: Array<{
  description: string;
  icon: React.ElementType;
  id: TippingPaymentOption;
  label: string;
}> = [
  {
    description: 'Card, Apple Pay, or Google Pay through Stripe',
    icon: CreditCardRoundedIcon,
    id: 'card',
    label: 'Card or wallet',
  },
  {
    description: 'Pay through Cash App',
    icon: CreditCardRoundedIcon,
    id: 'cash_app',
    label: 'Cash App Pay',
  },
];

interface FeedbackTipPaymentOptionsProps {
  copy?: Record<string, string>;
  onChange: (paymentOption: TippingPaymentOption) => void;
  paymentOption: TippingPaymentOption;
  labels?: Record<string, string>;
  helpText?: string;
}

const FeedbackTipPaymentOptions: React.FC<FeedbackTipPaymentOptionsProps> = ({
  copy,
  onChange,
  paymentOption,
  labels,
  helpText,
}) => (
  <Stack spacing={1}>
    <Typography variant="subtitle2" sx={{ textAlign: 'center', fontSize: 16, fontWeight: 600 }}>
      {copy?.['payment.methodTitle'] ?? 'Payment Method'}
    </Typography>
    {helpText ? <Typography variant="body2">{helpText}</Typography> : null}
    {paymentOptions.map((option) => {
      const selected = paymentOption === option.id;
      const OptionIcon = option.icon;

      return (
        <Button
          aria-pressed={selected}
          color="inherit"
          key={option.id}
          onClick={() => onChange(option.id)}
          sx={{
            bgcolor: 'var(--cx-brand-surface)',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: selected ? 'var(--cx-brand-text)' : 'var(--cx-brand-border)',
            color: 'var(--cx-brand-text)',
            borderRadius: '12px',
            textTransform: 'none',
            justifyContent: 'flex-start',
            minHeight: 60,
            pl: '10px',
            pr: '2px',
            py: '10px',
            '&:hover': {
              bgcolor: selected ? 'var(--cx-brand-surface)' : 'var(--cx-brand-primary-soft)',
              borderColor: selected ? 'var(--cx-brand-text)' : 'var(--cx-brand-border)',
            },
          }}
          type="button"
          variant="outlined"
        >
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: 'transparent',
              borderRadius: 1,
              color: 'var(--cx-brand-text)',
              display: 'flex',
              flexShrink: 0,
              fontSize: 22,
              height: 38,
              justifyContent: 'center',
              mr: '10px',
              width: 38,
            }}
          >
            {option.id === 'cash_app' ? (
              <img src={cashAppLogo} alt="" width={38} height={38} />
            ) : (
              <OptionIcon sx={{ fontSize: 24 }} />
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            <Typography
              variant="subtitle2"
              sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 600, color: 'var(--cx-brand-text)' }}
            >
              {labels?.[option.id] || option.label}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', fontSize: 12, lineHeight: '18px', color: 'var(--cx-brand-text-muted)' }}>
              {copy?.[
                option.id === 'card' ? 'payment.cardDescription' : 'payment.cashAppDescription'
              ] ?? option.description}
            </Typography>
          </Box>
          {selected ? (
            <Box aria-hidden="true" sx={{ width: 20, height: 20, flexShrink: 0, ml: 1,
              bgcolor: 'var(--cx-brand-text)',
              mask: `url(${JSON.stringify(feedbackRadioOn)}) center / contain no-repeat`,
              WebkitMask: `url(${JSON.stringify(feedbackRadioOn)}) center / contain no-repeat`,
            }} />
          ) : (
            <RadioButtonUncheckedIcon
              sx={{ fontSize: 20, ml: 1, color: 'var(--cx-brand-text-muted)' }}
            />
          )}
        </Button>
      );
    })}
  </Stack>
);

export default FeedbackTipPaymentOptions;
